import { gql } from '@apollo/client';
import { ChinaPageHomeMinifiedFragment, IChinaPageHome } from './chinaPageHome';
import {
  ILibraryMeetingPlaceMinified,
  ILibraryMeetingPlaceMinifiedExtended,
  ILibraryMeetingPlaceMinifiedExtendedAttributeFilters,
  ILibraryMeetingPlaceMinifiedLogo,
  ILibraryMeetingPlaceMinifiedOpeningHours,
  ILibraryMeetingPlaceSeoRichMinifiedExtended,
  LibraryMeetingPlaceAddressFragment,
  LibraryMeetingPlaceMinifiedExtendedAttributeFiltersFragment,
  LibraryMeetingPlaceMinifiedExtendedFragment,
  LibraryMeetingPlaceMinifiedFragment,
  LibraryMeetingPlaceMinifiedLogoFragment,
  LibraryMeetingPlaceMinifiedOpeningHoursFragment,
  LibraryMeetingPlaceSeoRichMinifiedExtendedFragment,
} from './libraryMeetingPlace';
import {
  ILibraryOpeningHours,
  LibraryOpeningHoursFragment,
} from './libraryOpeningHours';
import {
  ILibraryOpeningHoursException,
  LibraryOpeningHoursExceptionsFragment,
} from './libraryOpeningHoursException';
import { IMeetingPlacePageTenantShopList } from './meetingPlacePageTenantShopList';
import { IMeetingPlacePageTenantEntertainmentList } from './meetingPlacePageTenantEntertainmentList';
import { IMeetingPlacePageTenantFoodBeverageList } from './meetingPlacePageTenantFoodbeverageList';
import { IMeetingPlacePageTenantList } from './meetingPlacePageTenantList';

export interface IMeetingplacePageHomeMinified {
  sys: { id: string };
  breadcrumbHomeText: string;
  mediaField: {
    jsonBynderAsset: {
      originalUrl: string;
    };
  };
  meetingPlace: ILibraryMeetingPlaceMinified;
  reservedTagId?: string;
  htmlTitleSuffix?: string;
}

export interface IMeetingPlacePageHomeMinifiedExtendedTenantOpeningHours {
  sys: { id: string };
  breadcrumbHomeText: string;
  mediaField: {
    jsonBynderAsset: {
      originalUrl: string;
    };
  };
  reservedTagId?: string;
  htmlTitleSuffix?: string;
  meetingPlace: {
    timezone: string;
    integrationId: string;
    businessUnitCode: string;
    showSocialShare: boolean;
    openingHours: ILibraryOpeningHours;
    openingHoursExceptionsCollection: {
      items: ILibraryOpeningHoursException[];
    };
    tenantListPage?: IMeetingPlacePageTenantList;
    tenantShopListPage: IMeetingPlacePageTenantShopList;
    tenantFoodBeverageListPage?: IMeetingPlacePageTenantFoodBeverageList;
    tenantEntertainmentListPage?: IMeetingPlacePageTenantEntertainmentList;
  };
}

export interface IMeetingplacePageHomeMinifiedChina {
  sys: { id: string };
  breadcrumbHomeText: string;
  mediaField: {
    jsonBynderAsset: {
      originalUrl: string;
    };
  };
  meetingPlace: IChinaPageHome;
}

export interface IMeetingplacePageHomeMinifiedExtendedLogo extends IMeetingplacePageHomeMinified {
  meetingPlace: ILibraryMeetingPlaceMinifiedLogo & {
    country?: {
      countryCode?: string;
      name?: string;
    };
  };
}
export interface IMeetingplacePageHomeMinifiedExtendedAttributeFilters extends IMeetingplacePageHomeMinified {
  meetingPlace: ILibraryMeetingPlaceMinifiedExtendedAttributeFilters;
}
export interface IMeetingplacePageHomeMinifiedExtendedOpeningHours extends IMeetingplacePageHomeMinified {
  meetingPlace: ILibraryMeetingPlaceMinifiedOpeningHours;
}

export interface IMeetingplacePageHomeMinifiedExtendedHomePage extends IMeetingplacePageHomeMinifiedExtendedLogo {
  seoDescription: {
    seoDescription: string;
  };
  htmlTitleSuffix?: string;
  meetingPlace: ILibraryMeetingPlaceMinifiedExtended;
}

export interface IMeetingplacePageHomeSeoRichMinifiedExtendedHomePage extends IMeetingplacePageHomeMinifiedExtendedLogo {
  __typename: string;
  seoDescription: string;
  htmlTitleSuffix?: string;
  meetingPlace: ILibraryMeetingPlaceSeoRichMinifiedExtended;
}

export interface IMeetingplacePageHomeMinifiedExtendedHomePageChina extends IMeetingplacePageHomeMinifiedChina {
  seoDescription: {
    seoDescription: string;
  };
  htmlTitleSuffix?: string;
  meetingPlace: IChinaPageHome;
}

export const MeetingPlacePageHomeDataMinifiedFragment = gql`
  fragment MeetingPlacePageHomeDataMinified on MeetingPlacePageHome {
    sys {
      id
    }
    breadcrumbHomeText
    reservedTagId
    htmlTitleSuffix
    mediaField {
      jsonBynderAsset
    }
  }
`;

export const MeetingPlacePageHomeMinifiedFragment = gql`
  ${LibraryMeetingPlaceMinifiedFragment}
  ${LibraryMeetingPlaceAddressFragment}
  fragment MeetingPlacePageHomeMinified on MeetingPlacePageHome {
    sys {
      id
    }
    breadcrumbHomeText
    reservedTagId
    htmlTitleSuffix
    meetingPlace {
      ... on LibraryMeetingPlace {
        ...LibraryMeetingPlaceMinified
        ...LibraryMeetingPlaceAddress
        timezone
        name
      }
    }
    mediaField {
      jsonBynderAsset
    }
  }
`;

export const MeetingPlacePageHomeMinifiedExtendedLogoFragment = gql`
  ${LibraryMeetingPlaceMinifiedLogoFragment}
  fragment MeetingPlacePageHomeMinifiedExtendedLogo on MeetingPlacePageHome {
    sys {
      id
      locale
    }
    breadcrumbHomeText
    reservedTagId
    htmlTitleSuffix
    meetingPlace {
      ... on _Node {
        ...LibraryMeetingPlaceMinifiedLogo
      }
      ... on LibraryMeetingPlace {
        timezone
        country {
          countryCode
        }
      }
    }
    mediaField {
      jsonBynderAsset
    }
  }
`;

export const MeetingPlacePageHomeMinifiedExtendedTenantOpeningHoursFragment = gql`
  ${LibraryMeetingPlaceMinifiedFragment}
  ${LibraryOpeningHoursFragment}
  ${LibraryOpeningHoursExceptionsFragment}
  fragment MeetingPlacePageHomeMinifiedExtendedTenantOpeningHours on MeetingPlacePageHome {
    sys {
      id
    }
    breadcrumbHomeText
    reservedTagId
    htmlTitleSuffix
    mediaField {
      jsonBynderAsset
    }
    meetingPlace {
      ... on _Node {
        ...LibraryMeetingPlaceMinified
      }
      ... on LibraryMeetingPlace {
        timezone
        openingHours {
          ... on LibraryOpeningHours {
            ...LibraryOpeningHours
          }
        }
        openingHoursExceptionsCollection {
          items {
            ... on LibraryOpeningHoursException {
              ...LibraryOpeningHoursExceptions
            }
          }
        }
        # tenantListPage {
        #   tenantsCollection {
        #     items {
        #       ... on MeetingPlacePageTenantShop{
        #         sys {
        #           id
        #         }
        #         name
        #         slug
        #         # customizedSlug
        #         brand {
        #           name
        #           slug
        #         }
        #         openingHours {
        #           ...LibraryOpeningHours
        #         }
        #         openingHoursExceptionsCollection {
        #           items{
        #           ... on LibraryOpeningHoursException {
        #             ...LibraryOpeningHoursExceptions
        #           }
        #           }
        #         }
        #       }
        #       ... on MeetingPlacePageTenantEntertainment {
        #         sys {
        #           id
        #         }
        #         name
        #         slug
        #         # customizedSlug
        #         brand {
        #           name
        #           slug
        #         }
        #         openingHours {
        #           ...LibraryOpeningHours
        #         }
        #         openingHoursExceptionsCollection {
        #           items{
        #           ... on LibraryOpeningHoursException {
        #             ...LibraryOpeningHoursExceptions
        #           }
        #           }
        #         }
        #       }
        #       ... on MeetingPlacePageTenantFoodBeverage {
        #         sys {
        #           id
        #         }
        #         name
        #         slug
        #         # customizedSlug
        #         brand {
        #           name
        #           slug
        #         }
        #         openingHours {
        #           ...LibraryOpeningHours
        #         }
        #         openingHoursExceptionsCollection {
        #           items{
        #           ... on LibraryOpeningHoursException {
        #             ...LibraryOpeningHoursExceptions
        #           }
        #           }
        #         }
        #       }
        #     }
        #   }
        # }
        # tenantShopListPage {
        #   shopItemsCollection {
        #     items {
        #       ... on MeetingPlacePageTenantShop {
        #         sys {
        #           id
        #         }
        #         name
        #         slug
        #         # customizedSlug
        #         brand {
        #           name
        #           slug
        #         }
        #         openingHours {
        #           ...LibraryOpeningHours
        #         }
        #         openingHoursExceptionsCollection {
        #           items{
        #           ... on LibraryOpeningHoursException {
        #             ...LibraryOpeningHoursExceptions
        #           }
        #         }
        #         }
        #       }
        #     }
        #   }
        # }
        # tenantFoodBeverageListPage {
        #   foodBeverageItemsCollection {
        #     items{
        #     ... on MeetingPlacePageTenantFoodBeverage {
        #       sys {
        #         id
        #       }
        #       name
        #       slug
        #       # customizedSlug
        #       brand {
        #         name
        #         slug
        #       }
        #       openingHours {
        #         ...LibraryOpeningHours
        #       }
        #       openingHoursExceptionsCollection {
        #         items{
        #         ... on LibraryOpeningHoursException {
        #           ...LibraryOpeningHoursExceptions
        #         }
        #         }
        #       }
        #     }
        #   }
        #   }
        # }
        # tenantEntertainmentListPage {
        #   entertainmentItemsCollection {
        #     items{
        #     ... on MeetingPlacePageTenantEntertainment {
        #       sys {
        #         id
        #       }
        #       name
        #       slug
        #       # customizedSlug
        #       brand {
        #         name
        #         slug
        #       }
        #       openingHours {
        #         ...LibraryOpeningHours
        #       }
        #       openingHoursExceptionsCollection {
        #         items{
        #         ... on LibraryOpeningHoursException {
        #           ...LibraryOpeningHoursExceptions
        #         }
        #       }
        #       }
        #     }
        #   }
        #   }
        # }
      }
    }
  }
`;

export const MeetingPlacePageHomeMinifiedExtendedOpeningHoursFragment = gql`
  ${LibraryMeetingPlaceMinifiedOpeningHoursFragment}
  fragment MeetingPlacePageHomeMinifiedExtendedOpeningHours on MeetingPlacePageHome {
    sys {
      id
    }
    breadcrumbHomeText
    reservedTagId
    htmlTitleSuffix
    meetingPlace {
      ... on _Node {
        ...LibraryMeetingPlaceMinifiedOpeningHours
      }
    }
    mediaField {
      jsonBynderAsset
    }
  }
`;

export const MeetingplacePageHomeMinifiedExtendedAttributeFiltersFragment = gql`
  ${LibraryMeetingPlaceMinifiedLogoFragment}
  ${LibraryMeetingPlaceMinifiedExtendedAttributeFiltersFragment}
  ${LibraryMeetingPlaceAddressFragment}
  fragment MeetingplacePageHomeMinifiedExtendedAttributeFilters on MeetingPlacePageHome {
    sys {
      id
    }
    breadcrumbHomeText
    reservedTagId
    htmlTitleSuffix
    meetingPlace {
      ... on _Node {
        ...LibraryMeetingPlaceMinifiedLogo
        ...LibraryMeetingPlaceMinifiedExtendedAttributeFilters
        ...LibraryMeetingPlaceAddress
      }
    }
    mediaField {
      jsonBynderAsset
    }
  }
`;
export const MeetingPlacePageHomeMinifiedExtendedHomePageFragment = gql`
  ${LibraryMeetingPlaceMinifiedExtendedFragment}
  ${ChinaPageHomeMinifiedFragment}
  fragment MeetingPlacePageHomeMinifiedExtendedHomePage on MeetingPlacePageHome {
    sys {
      id
    }
    breadcrumbHomeText
    reservedTagId
    node_locale
    seoDescription
    meetingPlace {
      ... on Node {
        ...LibraryMeetingPlaceMinifiedExtended
        ...ChinaPageHomeMinified
      }
    }
    mediaField {
      jsonBynderAsset
    }
    htmlTitleSuffix
  }
`;

export const MeetingPlacePageHomeSeoRichMinifiedExtendedHomePageFragment = gql`
  ${LibraryMeetingPlaceSeoRichMinifiedExtendedFragment}
  fragment MeetingPlacePageHomeSeoRichMinifiedExtendedHomePage on MeetingPlacePageHome {
    sys {
      id
    }
    __typename
    breadcrumbHomeText
    reservedTagId
    seoDescription
    meetingPlace {
      ... on _Node {
        ...LibraryMeetingPlaceSeoRichMinifiedExtended
      }
    }
    mediaField {
      jsonBynderAsset
    }
    htmlTitleSuffix
  }
`;
